import React, { useState, useEffect } from 'react';
import { GitBranch, Tag, Calendar, Database, HardDrive, Server, CheckCircle, AlertCircle, RefreshCw, Package, Code2, Layers } from 'lucide-react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

// Static app version info — sourced from package.json / build metadata
const APP_VERSION = {
  version: '0.1.0',
  buildNumber: '20260325',
  buildDate: '2026-03-25',
  environment: import.meta.env?.MODE || 'production',
  branch: 'main',
  commitHash: 'a3f9c12',
  releaseChannel: 'stable',
};

// Migration changelog — derived from supabase/migrations filenames (static)
const MIGRATION_HISTORY = [
  { id: '20260325000000', name: 'Remove grace period dead code', date: '2026-03-25' },
  { id: '20260324220000', name: 'Fix phase logic and analytics', date: '2026-03-24' },
  { id: '20260322190000', name: 'Daily inspirations', date: '2026-03-22' },
  { id: '20260322020000', name: 'Fix study note comments RLS', date: '2026-03-22' },
  { id: '20260321000000', name: 'Verse highlights', date: '2026-03-20' },
  { id: '20260320164200', name: 'Add scripture scholar & perfect practice awards', date: '2026-03-20' },
  { id: '20260318220000', name: 'Fix realtime study notes', date: '2026-03-18' },
  { id: '20260318200000', name: 'Sermon study notes', date: '2026-03-18' },
  { id: '20260318110000', name: 'Blog newsletter subscribers', date: '2026-03-18' },
  { id: '20260317240000', name: 'Ensure has_strongs column', date: '2026-03-17' },
];

// Key tables to query for real row counts
const KEY_TABLES = [
  'bible_verses',
  'verses',
  'user_profiles',
  'review_history',
  'verse_reflections',
  'bible_versions',
  'bible_books',
  'award_definitions',
  'blog_posts',
  'study_notes',
  'reading_plans',
  'user_activities',
];

const formatCount = (count) => {
  if (count === null || count === undefined) return '—';
  if (count >= 1000000) return `${(count / 1000000)?.toFixed(1)}M`;
  if (count >= 1000) return `${(count / 1000)?.toFixed(1)}K`;
  return count?.toLocaleString();
};

const formatBytes = (kb) => {
  if (kb >= 1024 * 1024) return `${(kb / (1024 * 1024))?.toFixed(1)} GB`;
  if (kb >= 1024) return `${(kb / 1024)?.toFixed(1)} MB`;
  return `${kb} KB`;
};

const StatusBadge = ({ status }) => {
  const map = {
    healthy: { color: 'text-success bg-success/10 border-success/20', label: 'Healthy' },
    warning: { color: 'text-warning bg-warning/10 border-warning/20', label: 'Warning' },
    error: { color: 'text-destructive bg-destructive/10 border-destructive/20', label: 'Error' },
  };
  const s = map?.[status] || map?.healthy;
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${s?.color}`}>
      {s?.label}
    </span>
  );
};

const SystemInfoPanel = () => {
  const [activeTab, setActiveTab] = useState('version');
  const [lastRefreshed, setLastRefreshed] = useState(new Date()?.toLocaleTimeString());
  const [dbLoading, setDbLoading] = useState(false);
  const [dbError, setDbError] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [dbStats, setDbStats] = useState({
    tableCount: null,
    totalRows: null,
    migrationCount: 98,
    lastMigration: '2026-03-25',
  });

  const fetchDatabaseHealth = async () => {
    setDbLoading(true);
    setDbError(null);
    try {
      // Fetch real row counts for each key table in parallel
      const results = await Promise.all(
        KEY_TABLES?.map(async (tableName) => {
          try {
            const { count, error } = await supabase?.from(tableName)?.select('*', { count: 'exact', head: true });
            return {
              name: tableName,
              rowCount: error ? null : count,
              status: error ? 'warning' : 'healthy',
              error: error?.message || null,
            };
          } catch {
            return { name: tableName, rowCount: null, status: 'warning', error: 'Query failed' };
          }
        })
      );

      setTableData(results);

      // Compute total rows and table count from results
      const validRows = results?.filter((r) => r?.rowCount !== null);
      const totalRows = validRows?.reduce((sum, r) => sum + (r?.rowCount || 0), 0);

      setDbStats((prev) => ({
        ...prev,
        tableCount: results?.length,
        totalRows,
      }));

      setLastRefreshed(new Date()?.toLocaleTimeString());
    } catch (err) {
      setDbError(err?.message || 'Failed to fetch database health data');
    } finally {
      setDbLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'database') {
      fetchDatabaseHealth();
    }
  }, [activeTab]);

  const tabs = [
    { id: 'version', label: 'Version Info', icon: Tag },
    { id: 'database', label: 'Database Health', icon: Database },
    { id: 'migrations', label: 'Migrations', icon: Layers },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">System Information</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Code versioning, database health, and schema overview
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <RefreshCw className="w-3.5 h-3.5" />
          <span>Last refreshed: {lastRefreshed}</span>
        </div>
      </div>
      {/* Sub-tabs */}
      <div className="flex gap-1 border-b border-border">
        {tabs?.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap -mb-px ${
              activeTab === id
                ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon className="w-4 h-4" />
            {label}
          </button>
        ))}
      </div>
      {/* Version Info Tab */}
      {activeTab === 'version' && (
        <div className="space-y-4">
          {/* Version Banner */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="bg-primary/10 p-2.5 rounded-full">
                <Code2 className="w-5 h-5 text-primary" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Application Version</p>
                <p className="text-2xl font-bold text-foreground">v{APP_VERSION?.version}</p>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-success/10 text-success border border-success/20">
                <CheckCircle className="w-3 h-3" />
                {APP_VERSION?.releaseChannel}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-accent/10 text-accent border border-accent/20">
                {APP_VERSION?.environment}
              </span>
            </div>
          </div>

          {/* Version Details Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Tag className="w-4 h-4 text-muted-foreground" />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Build Number</p>
              </div>
              <p className="text-lg font-semibold text-foreground font-mono">{APP_VERSION?.buildNumber}</p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Build Date</p>
              </div>
              <p className="text-lg font-semibold text-foreground">{APP_VERSION?.buildDate}</p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <GitBranch className="w-4 h-4 text-muted-foreground" />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Branch</p>
              </div>
              <p className="text-lg font-semibold text-foreground font-mono">{APP_VERSION?.branch}</p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Code2 className="w-4 h-4 text-muted-foreground" />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Commit Hash</p>
              </div>
              <p className="text-lg font-semibold text-foreground font-mono">{APP_VERSION?.commitHash}</p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Server className="w-4 h-4 text-muted-foreground" />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Environment</p>
              </div>
              <p className="text-lg font-semibold text-foreground capitalize">{APP_VERSION?.environment}</p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-4 h-4 text-muted-foreground" />
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Release Channel</p>
              </div>
              <p className="text-lg font-semibold text-foreground capitalize">{APP_VERSION?.releaseChannel}</p>
            </div>
          </div>

          {/* Tech Stack */}
          <div className="bg-card border border-border rounded-lg p-4">
            <h3 className="text-sm font-semibold text-foreground mb-3">Core Technology Stack</h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {[
                { name: 'React', version: '18.2.0', color: 'bg-blue-500/10 text-blue-600 border-blue-200' },
                { name: 'Vite', version: '5.x', color: 'bg-purple-500/10 text-purple-600 border-purple-200' },
                { name: 'Tailwind CSS', version: '3.x', color: 'bg-cyan-500/10 text-cyan-600 border-cyan-200' },
                { name: 'Supabase', version: '2.x', color: 'bg-green-500/10 text-green-600 border-green-200' },
                { name: 'React Router', version: '6.0.2', color: 'bg-red-500/10 text-red-600 border-red-200' },
                { name: 'Redux Toolkit', version: '2.6.1', color: 'bg-violet-500/10 text-violet-600 border-violet-200' },
                { name: 'Framer Motion', version: '10.x', color: 'bg-pink-500/10 text-pink-600 border-pink-200' },
                { name: 'Lucide React', version: '0.484.0', color: 'bg-orange-500/10 text-orange-600 border-orange-200' },
              ]?.map(({ name, version, color }) => (
                <div key={name} className={`flex items-center justify-between px-3 py-2 rounded-lg border text-xs font-medium ${color}`}>
                  <span>{name}</span>
                  <span className="opacity-70">v{version}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {/* Database Health Tab */}
      {activeTab === 'database' && (
        <div className="space-y-4">
          {/* Refresh Button */}
          <div className="flex justify-end">
            <button
              onClick={fetchDatabaseHealth}
              disabled={dbLoading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-muted-foreground border border-border rounded-lg hover:bg-muted/40 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${dbLoading ? 'animate-spin' : ''}`} />
              {dbLoading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>

          {/* Error Banner */}
          {dbError && (
            <div className="flex items-start gap-2 bg-destructive/10 border border-destructive/20 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
              <p className="text-xs text-destructive">{dbError}</p>
            </div>
          )}

          {/* Health Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="bg-success/10 w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2">
                <CheckCircle className="w-5 h-5 text-success" />
              </div>
              <p className="text-xs text-muted-foreground font-medium">Status</p>
              <p className="text-sm font-bold text-success mt-0.5">
                {dbLoading ? '…' : dbError ? 'Degraded' : 'Operational'}
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="bg-accent/10 w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Database className="w-5 h-5 text-accent" />
              </div>
              <p className="text-xs text-muted-foreground font-medium">Tables Queried</p>
              <p className="text-sm font-bold text-foreground mt-0.5">
                {dbLoading ? '…' : dbStats?.tableCount ?? '—'}
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="bg-primary/10 w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2">
                <HardDrive className="w-5 h-5 text-primary" />
              </div>
              <p className="text-xs text-muted-foreground font-medium">Total Rows</p>
              <p className="text-sm font-bold text-foreground mt-0.5">
                {dbLoading ? '…' : dbStats?.totalRows !== null ? formatCount(dbStats?.totalRows) : '—'}
              </p>
            </div>

            <div className="bg-card border border-border rounded-lg p-4 text-center">
              <div className="bg-warning/10 w-10 h-10 rounded-full flex items-center justify-center mx-auto mb-2">
                <Layers className="w-5 h-5 text-warning" />
              </div>
              <p className="text-xs text-muted-foreground font-medium">Migrations</p>
              <p className="text-sm font-bold text-foreground mt-0.5">{dbStats?.migrationCount}</p>
            </div>
          </div>

          {/* Table Overview */}
          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <h3 className="text-sm font-semibold text-foreground">Table Row Counts (Live)</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Real-time row counts queried directly from Supabase</p>
            </div>
            {dbLoading ? (
              <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground text-sm">
                <RefreshCw className="w-4 h-4 animate-spin" />
                <span>Querying database…</span>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/40 border-b border-border">
                      <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground">Table</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Row Count</th>
                      <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {tableData?.map((table) => (
                      <tr key={table?.name} className="hover:bg-muted/20 transition-colors">
                        <td className="px-4 py-2.5 font-mono text-xs text-foreground">{table?.name}</td>
                        <td className="px-4 py-2.5 text-right text-xs text-foreground font-medium">
                          {table?.rowCount !== null ? formatCount(table?.rowCount) : (
                            <span className="text-muted-foreground italic text-xs">{table?.error || 'N/A'}</span>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <StatusBadge status={table?.status} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Live data note */}
          <div className="flex items-start gap-2 bg-success/5 border border-success/20 rounded-lg p-3">
            <CheckCircle className="w-4 h-4 text-success shrink-0 mt-0.5" />
            <p className="text-xs text-muted-foreground">
              Row counts are queried <strong>live from Supabase</strong> each time this tab is opened. Click <strong>Refresh</strong> to update.
            </p>
          </div>
        </div>
      )}
      {/* Migrations Tab */}
      {activeTab === 'migrations' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-xs text-muted-foreground font-medium mb-1">Total Migrations</p>
              <p className="text-2xl font-bold text-foreground">{dbStats?.migrationCount}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4">
              <p className="text-xs text-muted-foreground font-medium mb-1">Last Applied</p>
              <p className="text-lg font-bold text-foreground">{dbStats?.lastMigration}</p>
            </div>
            <div className="bg-card border border-border rounded-lg p-4 col-span-2 sm:col-span-1">
              <p className="text-xs text-muted-foreground font-medium mb-1">Schema Status</p>
              <div className="flex items-center gap-1.5 mt-1">
                <CheckCircle className="w-4 h-4 text-success" />
                <p className="text-sm font-bold text-success">Up to date</p>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-lg overflow-hidden">
            <div className="px-4 py-3 border-b border-border">
              <h3 className="text-sm font-semibold text-foreground">Recent Migrations</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Latest 10 schema migrations applied</p>
            </div>
            <div className="divide-y divide-border">
              {MIGRATION_HISTORY?.map((m, idx) => (
                <div key={m?.id} className="flex items-center justify-between px-4 py-3 hover:bg-muted/20 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full shrink-0 ${idx === 0 ? 'bg-primary' : 'bg-success'}`} />
                    <div>
                      <p className="text-sm font-medium text-foreground">{m?.name}</p>
                      <p className="text-xs text-muted-foreground font-mono">{m?.id}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="text-xs text-muted-foreground">{m?.date}</span>
                    <CheckCircle className="w-3.5 h-3.5 text-success" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemInfoPanel;
